import "./KDS.scss"

import React from "react"
import request from "request"
import usesFeatures from "usesFeatures"

import OrderCard from "./OrderCard"

// The live grid for the single station this device is logged in as. The station
// identity comes from the JWT (resolved server-side), so there is no station
// param and no picker. The header (KDSLayout) owns the station name, Recall
// nav, and Logout; this view is just the order grid.
class StationView extends React.Component {
  state = { items: [], station: null, venueId: null, recentlyChanged: new Set() }

  componentDidMount() {
    this.fetchItems()
  }

  componentWillUnmount() {
    if (this.subscription) this.subscription.unsubscribe()
  }

  fetchItems = () => {
    request.get("/kds").then(({ data }) => {
      this.setState({
        items: data.items || [],
        station: data.station || null,
        venueId: data.venueId,
      }, this.subscribe)
    })
  }

  subscribe = () => {
    if (this.subscription) this.subscription.unsubscribe()
    if (!this.state.venueId || !this.state.station) return

    this.subscription = this.props.subscribeTo(
      "KitchenDisplayChannel",
      { venueId: this.state.venueId, stationId: this.state.station.id },
      this.handleEvent,
    )
  }

  handleEvent = ({ event, item }) => {
    if (!item) return

    this.setState((prev) => {
      const filtered = prev.items.filter((i) => i.id !== item.id)
      const recentlyChanged = new Set(prev.recentlyChanged).add(item.id)

      // Dismiss/bump-to-ready: remove from outstanding view.
      const isOutstanding = ["fired", "partial", "recalled"].includes(item.kdsState)
      const next = isOutstanding ? [...filtered, item] : filtered

      return { items: next, recentlyChanged }
    })

    if (event === "item_fired" && this.props.playSound) this.props.playSound()

    // Clear flash marker after the animation has played.
    setTimeout(() => {
      this.setState((prev) => {
        const recentlyChanged = new Set(prev.recentlyChanged)
        recentlyChanged.delete(item.id)
        return { recentlyChanged }
      })
    }, 1200)
  }

  bump = (item) => {
    request.post(`kds/${item.id}/bump`).then(({ data }) => this.applyItemUpdate(data.item))
  }

  bumpAll = (item) => {
    request.post(`kds/${item.id}/bump_all`).then(({ data }) => this.applyItemUpdate(data.item))
  }

  dismiss = (items) => {
    items.forEach((item) => {
      request.post(`kds/${item.id}/dismiss`).then(({ data }) => this.applyItemUpdate(data.item))
    })
  }

  applyItemUpdate = (item) => {
    if (!item) return
    this.setState((prev) => {
      const filtered = prev.items.filter((i) => i.id !== item.id)
      const isOutstanding = ["fired", "partial", "recalled"].includes(item.kdsState)
      return { items: isOutstanding ? [...filtered, item] : filtered }
    })
  }

  groupByOrder = () => {
    const groups = new Map()
    this.state.items.forEach((item) => {
      const list = groups.get(item.orderId) || []
      list.push(item)
      groups.set(item.orderId, list)
    })
    // Stable order: oldest fired first.
    return Array.from(groups.entries()).sort((a, b) => {
      const aT = Math.min(...a[1].map((i) => new Date(i.kdsFiredAt || 0).getTime()))
      const bT = Math.min(...b[1].map((i) => new Date(i.kdsFiredAt || 0).getTime()))
      return aT - bT
    })
  }

  render() {
    const { station } = this.state
    const groups = this.groupByOrder()

    return (
      <div className="KDSApp">
        {groups.length === 0 ? (
          <div className="empty-state">All clear. Orders will appear here as they fire.</div>
        ) : (
          <div className="KDSGrid">
            {groups.map(([orderId, items]) => (
              <OrderCard
                key={orderId}
                items={items}
                prepTimeSeconds={station?.prepTimeSeconds}
                flash={items.some((i) => this.state.recentlyChanged.has(i.id))}
                onAction={this.bump}
                onActionAll={this.bumpAll}
                onDismiss={this.dismiss}
              />
            ))}
          </div>
        )}
      </div>
    )
  }
}

export default usesFeatures(["websockets", "sound"], StationView)
